import { Injectable } from "@angular/core";
import { AbstractControl, FormArray, FormControl } from "@angular/forms";
import moment from "moment";
import { ChipType } from "../models/table-data.model";
import {
  DatasetVersion,
  VersionDirectory,
  VersionFile,
} from "../models/dataset.model";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatDialog } from "@angular/material/dialog";
import { BehaviorSubject } from "rxjs/internal/BehaviorSubject";
import { Subscription } from "rxjs";
import { ModelVersion } from "src/models/models.model";
import { ApiResponse } from "src/models/api-response.model";
import { AlertService } from "./alert.service";
import { environment } from "src/environments/environment";

export type ReviewStatus = "Pending" | "Approved" | "Rejected";

@Injectable({
  providedIn: "root",
})
export class UtilityService {
  private isAdmin!: boolean;
  private isContributor!: boolean;
  private stateSubject = new BehaviorSubject<any>({
    datasetCurrpage: 1,
    modelCurrpage: 1,
    usecaseCurrpage: 1,
    datasetSearchText: "",
    modelSearchText: "",
    usecaseSearchText: "",
  });
  public state$ = this.stateSubject.asObservable();
  public subscriptions: Subscription[] = [];

  constructor(
    private alertService: AlertService,
    private matSnackBar: MatSnackBar,
    private matDialog: MatDialog
  ) {}

  /**
   * Method that returns current date in `DD-MM-YYYY HH:MM:SS` format.
   */
  getCurrentTS() {
    const currentTime = new Date();
    const currentDate = this.convertTo2Digit(String(currentTime.getDate()));
    const currentMonth = this.convertTo2Digit(
      String(currentTime.getMonth() + 1)
    );
    const currentYear = this.convertTo2Digit(String(currentTime.getFullYear()));
    const currentHour = this.convertTo2Digit(String(currentTime.getHours()));
    const currentMinutes = this.convertTo2Digit(
      String(currentTime.getMinutes())
    );
    const currentSecs = this.convertTo2Digit(String(currentTime.getSeconds()));

    return `${currentDate}-${currentMonth}-${currentYear} ${currentHour}:${currentMinutes}:${currentSecs}`;
  }

  /**
   * Method to convert a single digit number (if provided) to double digit.
   * @param num - Single digit number.
   * @returns A double digit number.
   */
  convertTo2Digit(num: string) {
    return num.length > 1 ? num : `0${num}`;
  }

  /**
   * Method to check whether web-app is running on localhost or not.
   * @returns A boolean value.
   */
  get isLocalEnv() {
    return (
      window.location.hostname.includes("localhost") ||
      window.location.hostname.includes("127.0.0.1")
    );
  }

  /**
   * Method used to get nested value of an object.
   * @param object - Object from which nested value is to be extracted.
   * @param nestedKey - "." separated key indicating the nesting.
   * @returns Value extracted from the object (if any).
   */
  getNestedValue(object: any, nestedKey: string) {
    let value: any;
    const keys = nestedKey.split(".");

    keys.forEach((key) => {
      value = object?.[key];
    });

    return value;
  }

  /**
   * Method used to enfore numeric input to a text field.
   * @param event - Event of for the keyboard event.
   * @return A boolean value indicating to allow the input or not.
   */
  validateNumericInput(event: KeyboardEvent) {
    return !isNaN(+event.key);
  }

  /**
   * Method used to check whether the provided string is a substring or not.
   * @param firstStr The string which contains the substring.
   * @param secondStr The substring which is to be checked.
   * @returns - A boolean value indicating the result.
   */
  checkInString(firstStr: any, secondStr: any) {
    return String(firstStr || "")
      .toLowerCase()
      .trim()
      .includes(
        String(secondStr || "")
          .toLowerCase()
          .trim()
      );
  }

  $formControl(control: AbstractControl) {
    return <FormControl>control;
  }

  get isRoleContributor(): boolean {
    return this.isContributor;
  }

  get isRoleOrganisationAdmin(): boolean {
    return this.isAdmin;
  }

  getChipType(actionType: string): ChipType {
    switch (actionType) {
      case "download":
      case "approved":
      case "close":
      case "resolved":
      case "ready":
        return ChipType.success;
      case "viewed":
      case "open":
      case "reopen":
        return ChipType.gray;
      case "pending":
      case "in progress":
        return ChipType.warning;
      case "draft":
      case "review":
        return ChipType.draft;
      case "escalated":
      case "rejected":
      case "delete":
        return ChipType.error;
      default:
        return ChipType.gray;
    }
  }

  /**
   * Method to sort list based on any particular key
   * @param listToBeSorted - list that need to be sorted
   * @param sortingKey -  key based on which list need to be sorted
   * @returns - Sorted list
   */
  sortListData<T>(listToBeSorted: T[], sortingKey: string): T[] {
    return listToBeSorted.sort((a: any, b: any) =>
      a[sortingKey] < b[sortingKey] ? -1 : 1
    );
  }

  /**
   * Method used to download PDF base64 on web or native-device.
   * @param base64 - Base64 of the PDF file.
   * @param fileName - Filename of the PDF.
   */
  downloadPDFBase64(base64: string, fileName: string) {
    const base64Url = `data:application/pdf;base64, ${base64}`;
    const downloadLinkElm = document.createElement("a");
    const docName = fileName + ".pdf";
    downloadLinkElm.href = base64Url;
    downloadLinkElm.download = docName;
    downloadLinkElm.click();
  }

  downloadBlob(blob: Blob, filename: string, successCB?: Function) {
    const downloadHelper = () => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
      successCB?.();
    };

    const reader = new FileReader();

    reader.onerror = downloadHelper;

    reader.onload = () => {
      try {
        const result: ApiResponse<any> = JSON.parse(reader.result as string);
        this.alertService.showSnackBar(result.header.msg);
      } catch (error) {
        downloadHelper();
      }
    };

    reader.readAsText(blob);
  }

  scrollIntoViewWithOffset(element: HTMLElement, offset = 120) {
    const offsetTop =
      element.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({
      top: offsetTop,
      behavior: "smooth",
    });
  }

  getDialogPosition(
    event: MouseEvent,
    dialogWidth: number,
    dialogHeight: number,
    padding: number = 10
  ): { top: string; left: string } {
    // Get mouse position
    const mouseX = event.clientX;
    const mouseY = event.clientY;

    // Get screen dimensions
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    // Calculate default position
    let top = mouseY;
    let left = mouseX;

    // Adjust for overflow on the right
    if (mouseX + dialogWidth + padding > screenWidth) {
      left = screenWidth - dialogWidth - padding;
    }

    // Adjust for overflow on the bottom
    if (mouseY + dialogHeight + padding > screenHeight) {
      top = screenHeight - dialogHeight - padding;
    }

    // Adjust for overflow on the left
    if (mouseX < padding) {
      left = padding;
    }

    // Adjust for overflow on the top
    if (mouseY < padding) {
      top = padding;
    }

    // Return position as strings for MatDialog configuration
    return {
      top: `${top}px`,
      left: `${left}px`,
    };
  }

  $versionFile(data: any) {
    return <VersionFile>data;
  }

  $versionDirectory(data: any) {
    return <VersionDirectory>data;
  }

  $modelVersion(data: any) {
    return <ModelVersion>data;
  }

  $datasetVersion(data: any) {
    return <DatasetVersion>data;
  }

  $formArray(control: AbstractControl) {
    return <FormArray>control;
  }

  checkIsMoreThan5Days(requestedDate: Date) {
    if (!requestedDate) return false;
    const createdMoment = moment(requestedDate);
    const currentMoment = moment();

    if (currentMoment.diff(createdMoment, "days") >= 1) {
      return true;
    } else {
      return false;
    }
  }

  subtractDays(date: Date, daysCount: number) {
    const updatedDate = new Date(date);
    updatedDate.setDate(updatedDate.getDate() - daysCount);
    return updatedDate;
  }

  copyText(entityName: string, text: string) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.alertService.showSnackBar(`${entityName} copied successfully!`);
      })
      .catch((_) => {
        this.alertService.showSnackBar(
          `An error occurred while copying ${entityName}`
        );
      });
  }

  openExternalPage(redirectionUrl: string) {
    window.open(redirectionUrl, "_self");
  }

  //Stop Pending api calls before we proceed to another API Calls especially for tabs
  stopPendingCalls() {
    if (this.subscriptions.length) {
      this.subscriptions.forEach((s) => s.unsubscribe());
      this.subscriptions = [];
    }
  }

  cleanHtml(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // List of attributes to remove
    const attributesToRemove = ["style", "width", "height"];

    attributesToRemove.forEach((attr) => {
      doc
        .querySelectorAll(`[${attr}]`)
        .forEach((el) => el.removeAttribute(attr));
    });

    return doc.body.innerHTML; // Return cleaned HTML
  }
  getState(): any {
    return this.stateSubject.getValue();
  }
  setState(newState: any): void {
    this.stateSubject.next(newState);
  }
  updateStateProperty(key: string, value: any): void {
    const currentState = this.getState();
    this.setState({ ...currentState, [key]: value });
  }

  viewUserManual() {
    window.open(`https://aikosh.indiaai.gov.in/manual/User_Manual_AIKosh.pdf`),
      "_blank";
    return;
  }

  clearFiltersFromStorage() {
    localStorage.removeItem("dataset-filter");
    localStorage.removeItem("model-filter");
    localStorage.removeItem("usecase-filter");
  }

  getPublishInfoMsg(roles: string[], arifactName: string) {
    const defaultMsg = "Contact Your Platform Admin for approval";
    const hasAdmin = roles.includes("org-admin");
    const hasContributor = roles.includes("contributor");

    if (hasAdmin && hasContributor) {
      return defaultMsg;
    } else if (hasAdmin) {
      return defaultMsg;
    } else if (hasContributor) {
      return `Contact Your Organization Admin for approval, post which platform admin will approve to publish ${arifactName}`;
    } else {
      return "";
    }
  }
  getTabLabelExcludingCount(str: string) {
    let result = str?.replace(/\s*\(\d+\)/, "");
    return result?.trim();
  }
  toggleBookmarkById(id: string, datasetList: any[]) {
    return datasetList.map((item) =>
      item.id === id ? { ...item, isBookmarked: !item.isBookmarked } : item
    );
  }

  /**
   * Method to remove blank fields
   * @param dataToFormat - Data that needs to be formatted
   * @returns formatted value after removing black fields
   */
  removeBlankFields<T extends Record<string, any>, V>(dataToFormat: T): V {
    const formattedData = Object.fromEntries(
      Object.entries(dataToFormat).filter(
        ([key, value]: [string, any]) =>
          value !== null &&
          value !== undefined &&
          value !== "" &&
          (!Array.isArray(value) || value.length > 0) &&
          (typeof value !== "object" || Object.keys(value).length > 0)
      )
    );

    return <V>formattedData;
  }

  formatDate = (date: string | moment.Moment | Date | undefined) => {
    if (!date) return undefined;
    return moment(date).format("YYYY-MM-DD");
  };

  appendToFormData(data: Record<string, any>, formData: FormData) {
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(
          key,
          typeof value === "object" ? JSON.stringify(value) : value.toString()
        );
      }
    });
  }

  getReviewStatus(statusObj: { status?: string }): ReviewStatus {
    const status = statusObj?.status;

    if (
      status === "Scheduled" ||
      status === "Ready" ||
      status === "Completed" ||
      status === "Ready for File Copy" ||
      status === "Launched"
    ) {
      return "Approved";
    } else if (
      status === "Rejected" ||
      status === "Expired" ||
      status === "Cancelled"
    ) {
      return "Rejected";
    }
    return "Pending";
  }

  isControlInvalid(controlName: AbstractControl) {
    return (
      (controlName.dirty && controlName.errors) ||
      (controlName.value && controlName.errors)
    );
  }

  /**
   * Generates correct full URL for files stored on the server
   */
  getResourceUrl(resUrl: string): string {
    if (!resUrl) return "";
    if (this.isValidUrl(resUrl)) return resUrl;

    // Clean URL to prevent double slashes
    const cleanPath = resUrl.startsWith("/") ? resUrl.substring(1) : resUrl;
    return `${environment.web_url}public/files/${cleanPath}`;
  }

  isValidUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch {
      return false;
    }
  }

  /**
   *
   * @param file File object to be validated
   * @param maxSizeMB max Limit in MB for the file size
   * @returns boolean value indicating whether the file size is valid and the actual file size in MB
   */
  isFileSizeValid(file: File, maxSizeMB: number): boolean {
    const sizeMB = file.size / (1024 * 1024);

    return sizeMB <= maxSizeMB;
  }
}
